import { useMemo, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../ConfirmDialog';
import { RefreshError } from '../RefreshError';
import {
  PROXY_ADMIN_BASE,
  labPath,
  useProxyLabs,
  useProxyWrite,
  type ProxyLab,
} from '../../hooks/useProxyAdmin';
import {
  ACTIONS_CELL,
  ROW_RULE,
  RowActions,
  TABLE,
  TableScroller,
  Toolbar,
  type View,
} from './AdminTable';
import { FieldDiff, JsonBlock } from './ChangePreview';
import { LabForm } from './LabForm';

const labText = (lab: ProxyLab) =>
  `${lab.name} ${lab.api} ${lab.base_url ?? ''} ${lab.api_key_name ?? ''} ${lab.provider ?? ''}`;

/**
 * One line, never wrapped: `https://` is implied (middleman requires it), the
 * host reads first, and the rest truncates, in full on hover.
 */
function BaseUrl({ url }: { url: string }) {
  const rest = url.replace(/^https:\/\//, '');
  const slash = rest.indexOf('/');
  const host = slash === -1 ? rest : rest.slice(0, slash);
  const path = slash === -1 ? '' : rest.slice(slash);
  return (
    <span className="block max-w-80 truncate" title={url}>
      <span className="text-ink">{host}</span>
      <span className="text-muted">{path}</span>
    </span>
  );
}

type Action =
  | { kind: 'create'; body: Record<string, unknown> }
  | { kind: 'update'; lab: ProxyLab; patch: Record<string, unknown> }
  | { kind: 'activate' | 'deactivate'; lab: ProxyLab };

const none = <span className="italic text-muted">none</span>;

/** The upstream APIs, URLs and keys models are routed to. */
export function ProxyLabs() {
  const [view, setView] = useState<View>('active');
  const labs = useProxyLabs(view);
  // Only this status's own rows: after a filter change the query is handed the
  // previous status's rows as placeholder data (kept if the request fails),
  // which would show active labs under Inactive, actions and all.
  const rows = labs.isPlaceholderData ? undefined : labs.data;
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shown = useMemo(
    () =>
      needle
        ? rows?.filter(lab => labText(lab).toLowerCase().includes(needle))
        : rows,
    [rows, needle]
  );
  const [editing, setEditing] = useState<ProxyLab | 'new' | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const { busy, error, send, reset } = useProxyWrite();
  const edit = (next: ProxyLab | 'new' | null) => {
    reset();
    setEditing(next);
  };
  const review = (action: Action) => {
    reset();
    setPending(action);
  };

  const confirm = async () => {
    if (!pending) return;
    const result =
      pending.kind === 'create'
        ? await send('POST', `${PROXY_ADMIN_BASE}/labs/`, pending.body)
        : pending.kind === 'update'
          ? await send('PATCH', labPath(pending.lab.name), pending.patch)
          : await send('POST', labPath(pending.lab.name, pending.kind));
    if (result === null) return;
    if (pending.kind === 'create' || pending.kind === 'update')
      setEditing(null);
    setPending(null);
  };

  return (
    <section>
      <p className="max-w-3xl text-muted">
        A lab says which passthrough API serves its models, where requests go
        and which provider key is sent. Model configs name their lab.
      </p>
      <Toolbar
        view={view}
        onView={setView}
        query={query}
        onQuery={setQuery}
        noun="lab"
        count={rows && shown && { shown: shown.length, total: rows.length }}
      >
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => edit('new')}
        >
          Add lab
        </button>
      </Toolbar>

      {editing && (
        <LabForm
          key={editing === 'new' ? 'new' : editing.name}
          lab={editing === 'new' ? null : editing}
          busy={busy}
          onSubmit={body =>
            review(
              editing === 'new'
                ? { kind: 'create', body }
                : { kind: 'update', lab: editing, patch: body }
            )
          }
          onCancel={() => edit(null)}
        />
      )}

      <div className="mt-3">
        {labs.error && !rows ? (
          <p className="text-err">{labs.error.message}</p>
        ) : !rows || !shown ? (
          <p className="text-muted">Loading labs…</p>
        ) : (
          <>
            <RefreshError
              error={labs.error}
              onRetry={() => void labs.refetch()}
            />
            {rows.length === 0 ? (
              <p className="text-muted">
                No {view === 'all' ? '' : `${view} `}labs.
              </p>
            ) : shown.length === 0 ? (
              <p className="text-muted">No lab matches.</p>
            ) : (
              <TableScroller>
                <table className={TABLE}>
                  <thead className="eyebrow">
                    <tr>
                      <th className="py-1.5 pr-4 font-semibold">Lab</th>
                      <th className="px-2 font-semibold">API</th>
                      <th className="px-2 font-semibold">Base URL</th>
                      <th className="px-2 font-semibold">Credential</th>
                      <th className="px-2 font-semibold">Key</th>
                      <th className="px-2 font-semibold">Provider</th>
                      <th className="px-2 font-semibold">Flags</th>
                      <th className={ACTIONS_CELL}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map(lab => (
                      <tr key={lab.name} className={`align-middle ${ROW_RULE}`}>
                        <td className="py-1.5 pr-4 font-mono text-ink">
                          {lab.name}
                        </td>
                        <td className="px-2 font-mono">{lab.api}</td>
                        <td className="px-2 font-mono">
                          {lab.base_url ? <BaseUrl url={lab.base_url} /> : none}
                        </td>
                        <td className="px-2">{lab.credential_kind ?? none}</td>
                        <td className="px-2 font-mono">
                          {lab.api_key_name ?? none}
                          {lab.high_priority_api_key_name && (
                            <span
                              className="block text-muted"
                              title="High-priority key"
                            >
                              {lab.high_priority_api_key_name}
                            </span>
                          )}
                        </td>
                        <td className="px-2">{lab.provider ?? none}</td>
                        <td className="space-x-1 px-2">
                          {!lab.is_active && (
                            <span className="badge">inactive</span>
                          )}
                          {!lab.is_chat && (
                            <span className="badge">not chat</span>
                          )}
                          {lab.unsupported_paths.length > 0 && (
                            <span
                              className="badge"
                              title={lab.unsupported_paths.join('\n')}
                            >
                              {lab.unsupported_paths.length} unsupported paths
                            </span>
                          )}
                        </td>
                        <td className={ACTIONS_CELL}>
                          <RowActions
                            name={lab.name}
                            active={lab.is_active}
                            busy={busy}
                            onEdit={() => edit(lab)}
                            onToggle={() =>
                              review({
                                kind: lab.is_active ? 'deactivate' : 'activate',
                                lab,
                              })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={pending && actionTitle(pending)}
        confirmLabel={
          pending
            ? {
                create: 'Add lab',
                update: 'Save changes',
                activate: 'Activate',
                deactivate: 'Deactivate',
              }[pending.kind]
            : ''
        }
        busyLabel="Saving…"
        destructive={pending?.kind === 'deactivate'}
        busy={busy}
        error={pending ? error : null}
        onConfirm={() => void confirm()}
        onClose={() => {
          setPending(null);
          reset();
        }}
      >
        {pending && <ActionPreview action={pending} />}
      </ConfirmDialog>
    </section>
  );
}

function actionTitle(action: Action): ReactNode {
  const name = (
    <span className="font-mono text-xs font-normal text-ink-2">
      {action.kind === 'create' ? String(action.body.name) : action.lab.name}
    </span>
  );
  const verb = {
    create: 'Add lab',
    update: 'Change lab',
    activate: 'Activate lab',
    deactivate: 'Deactivate lab',
  }[action.kind];
  return (
    <>
      {verb} {name}
    </>
  );
}

function ActionPreview({ action }: { action: Action }) {
  switch (action.kind) {
    case 'create':
      return (
        <>
          <p>
            Adds an active lab. The proxy refuses it unless the deployment’s
            key-host allowlist lets each key it names go to its host.
          </p>
          <JsonBlock value={action.body} label="Request body" />
        </>
      );
    case 'update':
      return (
        <FieldDiff
          before={action.lab as unknown as Record<string, unknown>}
          patch={action.patch}
        >
          <p className="mt-2">
            Every model on this lab is served by the changed route as soon as
            the proxy reloads.
          </p>
        </FieldDiff>
      );
    case 'deactivate':
      return (
        <p>
          No route serves this lab once it is deactivated. Refused while any
          active model uses it: move those models or deactivate them first.
        </p>
      );
    case 'activate':
      return (
        <p>
          The lab serves traffic again. It is re-checked against today’s rules
          and key-host allowlist first, so a lab written under older rules may
          be refused.
        </p>
      );
  }
}
